import React from 'react';
import { Button, Card, Stack, Typography } from '@mui/material';
import { ReceiptLong } from '@mui/icons-material';
import { isTableOrder, Order, OrderStatus } from '@api/Orders/Order';
import OrderActivityButton from '@components/Orders/OrderActivityButton';
import OrderPurgeButton from '@components/Orders/OrderPurgeButton';
import OrderLineDetails from '@screens/Orders/components/OrderLineDetails';

interface OrderProductsPanelProps {
    order: Order;
    /** Solo el admin (perfil REAL) ve "Eliminar"; el servicio vuelve a exigirlo. */
    canPurge: boolean;
    onPurged: () => void;
    onShowTicket: () => void;
    onModifyOrder: () => void;
    onCompleteOrder: () => void;
    onChangeStatusOrder: (newStatus: OrderStatus) => void;
    onSendMessageTicket: () => void;
}

/**
 * La columna derecha de la vista Lista: recibo, bitácora y borrado; las líneas
 * del pedido separadas en mostrador / para llevar, y las acciones de un pedido
 * de mostrador (retomar en el POS, completar, marcar listo, reenviar el ticket).
 * Presentacional: los dos "Completar pedido" de aquí van a `onCompleteOrder`,
 * que pasa por el gate del cobro.
 */
const OrderProductsPanel: React.FunctionComponent<OrderProductsPanelProps> = ({
    order,
    canPurge,
    onPurged,
    onShowTicket,
    onModifyOrder,
    onCompleteOrder,
    onChangeStatusOrder,
    onSendMessageTicket
}) => (
    <Stack component={Card} borderRadius={2} elevation={3} p={3} m={2} spacing={3} overflow="auto">
        <Stack direction="row" justifyContent="flex-end" spacing={1} flexWrap="wrap">
            {/* Recibo re-imprimible desde la orden persistida — F4 ③ */}
            <Button variant="outlined" startIcon={<ReceiptLong />} onClick={onShowTicket}>
                Recibo
            </Button>
            <OrderActivityButton order={order} />
            {/* Depuración de pedidos de prueba: hard-delete, solo admin */}
            {canPurge && <OrderPurgeButton order={order} onPurged={onPurged} />}
        </Stack>

        {order.products.filter((p) => !p?.takeAway).length > 0 && (
            <Stack spacing={2}>
                <Typography variant="h6" color="primary" fontWeight={600}>
                    🍽️ Para mostrador
                </Typography>
                {order.products
                    .filter((p) => !p?.takeAway)
                    .map((product, index) => (product ? <OrderLineDetails key={index} product={product} /> : null))}
            </Stack>
        )}

        {order.products.filter((p) => p?.takeAway).length > 0 && (
            <Stack spacing={2}>
                <Typography variant="h6" color="primary" fontWeight={600}>
                    🛍️ Para llevar
                </Typography>
                {order.products
                    .filter((p) => p?.takeAway)
                    .map((product, index) => (product ? <OrderLineDetails key={index} product={product} /> : null))}
            </Stack>
        )}

        <Stack spacing={2}>
            {!order.deliveryType &&
                order.status != OrderStatus.Completed &&
                order.status != OrderStatus.Cancelled &&
                order.status != OrderStatus.Paid && (
                    <Button onClick={onModifyOrder} variant="contained" fullWidth size="large">
                        Retomar pedido
                    </Button>
                )}

            {order.status === OrderStatus.Paid && !order.deliveryType && (
                <Button onClick={onCompleteOrder} variant="contained" color="success" fullWidth size="large">
                    Completar pedido
                </Button>
            )}

            {/* Un pedido de mostrador/kiosko que avanzó en el tablero tiene que
                poder cerrarse también desde aquí (antes solo existía el paso
                directo desde Pagado y se quedaba sin botón) */}
            {!order.deliveryType && !isTableOrder(order) && order.status === OrderStatus.Preparing && (
                <Button
                    onClick={() => onChangeStatusOrder(OrderStatus.Ready)}
                    variant="contained"
                    color="success"
                    fullWidth
                    size="large"
                >
                    Pedido listo
                </Button>
            )}

            {!order.deliveryType && !isTableOrder(order) && order.status === OrderStatus.Ready && (
                <Button onClick={onCompleteOrder} variant="contained" color="success" fullWidth size="large">
                    Completar pedido
                </Button>
            )}

            {!order.deliveryType && (
                <Button onClick={onSendMessageTicket} variant="outlined" fullWidth>
                    Enviar ticket (Ticket Message)
                </Button>
            )}
        </Stack>
    </Stack>
);

export default OrderProductsPanel;
